/**
 * The router.
 *
 * ── Why this exists at all ───────────────────────────────────────────────────
 *
 * `notify.ts` already models a notification `target` as a deep link and
 * `public/sw.js` already calls `client.navigate(target)` when someone taps a
 * push. A `useState<Tab>` cannot honour that: every push would land on the feed,
 * which silently destroys the whole alert product — the alert arrives, the tap
 * works, and the wrong screen opens, with nothing anywhere reporting a fault.
 *
 * So navigation is real paths over `history.pushState`, and all three
 * environments already set `not_found_handling: "single-page-application"` with
 * `run_worker_first: ["/api/*"]`, so a cold load of `/o/op_abc` serves the SPA
 * and lands on the opportunity.
 *
 * ── Why not react-router ─────────────────────────────────────────────────────
 *
 * Twelve kilobytes for four features. The template deliberately ships no router,
 * and everything below is one `useSyncExternalStore` over `popstate` plus a
 * `split('/')`. Hash routes were rejected too: they break the GET share target,
 * look wrong when someone pastes a link, and `client.navigate` on a hash-only
 * change does not reliably re-render.
 *
 * This file is `.ts`, not `.tsx`, so `Link` is built with `createElement`. That
 * is the whole cost of keeping the router out of the component tree.
 */

import { createElement, useMemo, useSyncExternalStore, type AnchorHTMLAttributes, type ReactNode } from 'react'

/** Every screen the shell can show. `notFound` is a real route, not a throw. */
export type RouteName =
  | 'feed'
  | 'opportunity'
  | 'inspect'
  | 'board'
  | 'compare'
  | 'map'
  | 'search'
  | 'intake'
  | 'inbox'
  | 'me'
  | 'settings'
  | 'setup'
  | 'admin'
  | 'notFound'

export interface Route {
  name: RouteName
  /** Pathname only, no query. `/o/op_abc`. */
  path: string
  /** `/o/:id` → `{ id }`; `/o/:id/inspect` → `{ id }`; `/setup/:section` → `{ section }`. */
  params: Readonly<Record<string, string>>
  query: URLSearchParams
}

/**
 * Pure, and exported so it can be tested without a DOM.
 *
 * Anything unrecognised is `notFound` rather than a silent fall-through to the
 * feed: a typo'd notification target must be visible, not absorbed.
 */
export function parsePath(href: string): Route {
  const cut = href.indexOf('?')
  const raw = cut === -1 ? href : href.slice(0, cut)
  const path = raw.startsWith('/') ? raw : `/${raw}`
  const query = new URLSearchParams(cut === -1 ? '' : href.slice(cut))
  const seg = path.split('/').filter(Boolean).map(decodeSegment)

  const at = (name: RouteName, params: Record<string, string> = {}): Route =>
    ({ name, path, params, query })

  if (seg.length === 0) return at('feed')

  if (seg[0] === 'o' && seg[1]) {
    if (seg.length === 2) return at('opportunity', { id: seg[1] })
    if (seg.length === 3 && seg[2] === 'inspect') return at('inspect', { id: seg[1] })
    return at('notFound')
  }

  // `/setup` is the index; `/setup/scoring` is a section. Both are one route,
  // because the section is a panel choice rather than a different screen.
  if (seg[0] === 'setup') {
    if (seg.length <= 2) return at('setup', seg[1] ? { section: seg[1] } : {})
    return at('notFound')
  }

  if (seg.length === 1) {
    switch (seg[0]) {
      case 'feed': return at('feed')
      case 'board': return at('board')
      case 'compare': return at('compare')
      case 'map': return at('map')
      case 'search': return at('search')
      case 'intake': return at('intake')
      case 'inbox': return at('inbox')
      case 'me': return at('me')
      case 'settings': return at('settings')
      case 'admin': return at('admin')
    }
  }

  return at('notFound')
}

function decodeSegment(s: string): string {
  // A malformed escape is a bad link, not a crash that blanks the app.
  try {
    return decodeURIComponent(s)
  } catch {
    return s
  }
}

const listeners = new Set<() => void>()

function read(): string {
  return typeof location === 'undefined' ? '/' : location.pathname + location.search
}

let snapshot = read()

/**
 * The snapshot is the href STRING, not a parsed object. `useSyncExternalStore`
 * compares snapshots with `Object.is`, so returning a freshly parsed object
 * every call would re-render on every store read, forever.
 */
function getSnapshot(): string {
  return snapshot
}

function announce(): void {
  const next = read()
  if (next === snapshot) return
  snapshot = next
  for (const fn of [...listeners]) fn()
}

if (typeof window !== 'undefined') {
  // Back and forward. `pushState` does not fire this, which is why `navigate`
  // announces for itself.
  window.addEventListener('popstate', () => {
    // Direction is not knowable from the event, so this is an approximation:
    // it is only ever used to decide whether a back button has somewhere of
    // OURS to go, and undercounting there is the safe way to be wrong.
    if (pushes > 0) pushes -= 1
    announce()
  })
}

/**
 * How many entries WE pushed.
 *
 * Someone who arrives by tapping a push lands directly on `/o/op_abc` with a
 * browser history full of entries that are not ours. `history.length > 1` is
 * true there, so a back button trusting it walks the user straight out of the
 * application — which reads as the app crashing.
 */
let pushes = 0

export function canGoBack(): boolean {
  return pushes > 0
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/** The current route. Re-renders the caller on every navigation. */
export function useRoute(): Route {
  const href = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return useMemo(() => parsePath(href), [href])
}

/** Where we are, for code that is not a component. */
export function currentPath(): string {
  return snapshot
}

export function navigate(to: string, options: { replace?: boolean } = {}): void {
  if (typeof history === 'undefined') return
  if (to === snapshot) return
  history[options.replace ? 'replaceState' : 'pushState']({}, '', to)
  if (!options.replace) pushes += 1
  announce()
  /*
   * A new screen starts at the top. The browser restores scroll for a real back
   * or forward and we must not fight that, so this only runs for a push — which
   * is exactly the case where the old scroll position is meaningless.
   */
  if (!options.replace && typeof window !== 'undefined') window.scrollTo(0, 0)
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  to: string
  replace?: boolean
  children?: ReactNode
}

/**
 * A real `<a href>`, so long-press, middle-click, "open in new tab" and the
 * status bar preview all behave. Only a plain left click is intercepted.
 */
export function Link({ to, replace, onClick, children, ...rest }: LinkProps) {
  return createElement(
    'a',
    {
      ...rest,
      href: to,
      onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event)
        if (event.defaultPrevented) return
        if (event.button !== 0) return
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        event.preventDefault()
        navigate(to, { replace })
      },
    },
    children,
  )
}
